import { findLast, sortBy } from 'lodash';
import { hToMs } from '../../lib/time';
import {
  type EventOrLeg,
  excludeDryDockPeriods,
  isDischargeEventTimeData,
  isLeg,
  isLoadEventTimeData,
  isPortCallArrivalEventTimeData,
  isPortCallDepartureEventTimeData,
  isTimeData,
} from '../core/TimeData';
import { type EventTimeline } from '../core/Timelines';

/**
 * Calculate financial bounds (start/end times) for a sequence of events and legs.
 *
 * Start: Initial ballast leg OR first load event
 * End: Last departure start time (if departing last discharge port without new shipment)
 *      OR last discharge end time (if no departure is found or new shipment is loaded in the same port)
 */
export function getEventsFinancialBounds(allEventsAndLegs: EventOrLeg[]): {
  startTime: number | null;
  endTime: number | null;
} {
  const allEvents = allEventsAndLegs.filter(isTimeData);

  // Start counting of days from initial ballast leg or first load event
  const firstItem = allEventsAndLegs.at(0);

  const startTime =
    (firstItem &&
      (isLeg(firstItem) && !firstItem.laden
        ? // When Leg, start of the leg is already available in 'start'
          firstItem.start
        : // When called on a TimeData array without Legs, need to calculate leg start from the first TimeData in the port call
          isTimeData(firstItem) &&
            (isPortCallArrivalEventTimeData(firstItem) || // Port call starts from arrival event...
              ('hoursAtSea' in firstItem && firstItem.hoursAtSea)) && // ... or a port call without added PortCallEvents, port call starts from any other TD with sea hours (may be a case e.g. in the suggestion engine)
            !firstItem.laden
          ? firstItem.start - hToMs(firstItem.hoursAtSea)
          : allEvents.find((td) => isLoadEventTimeData(td))?.start)) ||
    null;

  // End counting of days at start of last departure or end of last
  // discharge if no departure from last discharge port
  // If a new shipment is loaded during the last port call, end counting at last
  // discharge
  const lastDeparture = findLast(allEvents, isPortCallDepartureEventTimeData);
  const lastDischarge = findLast(allEvents, isDischargeEventTimeData);

  const newShipmentLoadedAtLastDischargePort =
    (lastDeparture?.event.shipmentCount ?? 1) > 1;

  const isLastDepartureFromLastDischargePort =
    lastDeparture &&
    lastDischarge &&
    lastDeparture.event.portCallId === lastDischarge.event.portCallId;

  const endTime = newShipmentLoadedAtLastDischargePort
    ? (lastDischarge?.end ?? null)
    : isLastDepartureFromLastDischargePort
      ? lastDeparture.start
      : (lastDischarge?.end ?? null);

  return {
    startTime,
    endTime,
  };
}

export const getTimelineBounds = (timeline: EventTimeline) => {
  const allEvents = sortBy(
    timeline.voyageGroups.flatMap((vg) => vg.eventsAndLegs),
    (td) => td.start,
  );

  // Separate TimeData and non-TimeData (legs), filter dry dock from TimeData, then recombine
  const timeDataEvents = allEvents.filter(isTimeData);
  const legs = allEvents.filter((e) => !isTimeData(e));
  const filtered = excludeDryDockPeriods(timeDataEvents);
  const allEventsAndLegs = sortBy([...filtered, ...legs], (td) => td.start);

  return getEventsFinancialBounds(allEventsAndLegs);
};
